/**
 * Read aloud (SEO_BRIEF_10 section 2.1 step 4): the browser's own `speechSynthesis`, with a
 * voice for the translation's language. Nothing is recorded or saved.
 *
 * THREE THINGS BROWSERS DO DIFFERENTLY, and what this does about each:
 *   - The voice list arrives late. Chrome hands back an empty `getVoices()` until its
 *     `voiceschanged` fires; Safari usually has it at once. `voicesReady` waits for the event,
 *     for VOICES_WAIT_MS at most, and then works with what there is.
 *   - Safari on an iPhone speaks only once something has been spoken from a tap. `prime()`
 *     says nothing (a silent space) the first time it is called, from the visitor's first tap
 *     on a microphone or the Read aloud switch; every reading after that may come from a
 *     translation arriving.
 *   - A browser can drop an utterance's `end` (some Android builds) or sit paused (Chrome
 *     after a tab comes back). Each reading resolves on `end`, on `error`, or after a time
 *     long enough for its words, whichever comes first, and a paused engine is resumed.
 *
 * A language with no voice in this browser is not read with another language's voice: `speak`
 * resolves false and the translator says so once. A browser that lists no voices at all is
 * asked to read anyway, in the language's tag, and picks its own.
 */

import { baseCode, liveLanguage } from './languages.ts';

/** The parts of a `SpeechSynthesisVoice` this module reads. */
export interface VoiceLike {
  readonly lang: string;
  readonly name: string;
  readonly default?: boolean;
  readonly localService?: boolean;
}

/** The parts of a `SpeechSynthesisUtterance` this module sets. */
export interface UtteranceLike {
  text: string;
  lang: string;
  voice: VoiceLike | null;
  volume: number;
  rate: number;
  onend: (() => void) | null;
  onerror: (() => void) | null;
}

export type UtteranceCtor = new (text: string) => UtteranceLike;

/** The parts of `speechSynthesis` this module uses. */
export interface SynthLike {
  getVoices(): readonly VoiceLike[];
  speak(utterance: UtteranceLike): void;
  cancel(): void;
  resume?(): void;
  readonly paused?: boolean;
  addEventListener?(type: 'voiceschanged', listener: () => void): void;
  removeEventListener?(type: 'voiceschanged', listener: () => void): void;
}

/** How long the voice list is waited for. */
export const VOICES_WAIT_MS = 1500;

const normal = (tag: string) => tag.replace(/_/g, '-').toLowerCase();

/** Cantonese voices are listed under zh too (zh-HK, yue); Mandarin translations skip them. */
const CANTONESE = /^(?:yue|zh-hk|zh-yue)/;

/**
 * The voice for `code` among `voices`: the first of the language's own voice tags that some
 * voice has (pt-BR before pt-PT), the browser's default among equals, then a voice on the
 * device before one fetched; then any voice of the same base code. Null when none speaks it.
 */
export function pickVoice(voices: readonly VoiceLike[], code: string): VoiceLike | null {
  const language = liveLanguage(code);
  const rank = (v: VoiceLike) => (v.default ? 0 : 2) + (v.localService === false ? 1 : 0);
  const best = (list: VoiceLike[]) => list.sort((x, y) => rank(x) - rank(y))[0] ?? null;
  for (const tag of language?.voices ?? []) {
    const want = normal(tag);
    const hit = best(voices.filter((v) => normal(v.lang) === want));
    if (hit) return hit;
  }
  return best(
    voices.filter(
      (v) => baseCode(v.lang) === code && !(code === 'zh' && CANTONESE.test(normal(v.lang))),
    ),
  );
}

export interface SpeechOut {
  /** Call from a tap: the first call unlocks reading on an iPhone. */
  prime(): void;
  /** Whether this browser has a voice for `code` (true when it lists no voices at all). */
  canSpeak(code: string): Promise<boolean>;
  /**
   * Read `text` in `code`. Resolves true once it has been read (or given up on), false at once
   * when this browser has no voice for `code`.
   */
  speak(text: string, code: string): Promise<boolean>;
  /** Stop reading, and drop what was waiting to be read. */
  cancel(): void;
}

export interface SpeechOutOptions {
  setTimeout?: (fn: () => void, ms: number) => unknown;
  clearTimeout?: (handle: unknown) => void;
  /** How long a reading may take before it is taken as finished: ms per character, plus a floor. */
  msPerChar?: number;
  floorMs?: number;
}

export function createSpeechOut(
  synth: SynthLike,
  Utterance: UtteranceCtor,
  options: SpeechOutOptions = {},
): SpeechOut {
  const later = options.setTimeout ?? ((fn, ms) => setTimeout(fn, ms));
  const cancelLater =
    options.clearTimeout ?? ((h) => clearTimeout(h as ReturnType<typeof setTimeout>));
  const msPerChar = options.msPerChar ?? 140;
  const floorMs = options.floorMs ?? 4000;
  let primed = false;
  let ready: Promise<readonly VoiceLike[]> | null = null;
  /** Readings in progress, each with the way to end it early. */
  const open = new Set<() => void>();

  function voicesReady(): Promise<readonly VoiceLike[]> {
    const now = safeVoices();
    if (now.length > 0) return Promise.resolve(now);
    if (ready) return ready;
    ready = new Promise((resolve) => {
      let done = false;
      const finish = () => {
        if (done) return;
        done = true;
        synth.removeEventListener?.('voiceschanged', onChange);
        resolve(safeVoices());
        // An empty list may fill later: the next reading asks again.
        if (safeVoices().length === 0) ready = null;
      };
      const onChange = () => {
        if (safeVoices().length > 0) finish();
      };
      synth.addEventListener?.('voiceschanged', onChange);
      later(finish, VOICES_WAIT_MS);
    });
    return ready;
  }

  function safeVoices(): readonly VoiceLike[] {
    try {
      return synth.getVoices() ?? [];
    } catch {
      return [];
    }
  }

  return {
    prime() {
      if (primed) return;
      primed = true;
      try {
        // Ask for the list now, so it is there by the first translation.
        void voicesReady();
        const hush = new Utterance(' ');
        hush.volume = 0;
        synth.speak(hush);
      } catch {
        /* a browser that refuses still reads later, or says it has no voice */
      }
    },
    async canSpeak(code) {
      const voices = await voicesReady();
      return voices.length === 0 || pickVoice(voices, code) !== null;
    },
    async speak(text, code) {
      const words = text.trim();
      if (!words) return true;
      const voices = await voicesReady();
      const voice = voices.length > 0 ? pickVoice(voices, code) : null;
      if (voices.length > 0 && !voice) return false;
      return new Promise<boolean>((resolve) => {
        let timer: unknown = null;
        const end = () => {
          if (!open.has(end)) return;
          open.delete(end);
          if (timer !== null) cancelLater(timer);
          resolve(true);
        };
        open.add(end);
        try {
          const utterance = new Utterance(words);
          utterance.lang = voice?.lang.replace(/_/g, '-') ?? liveLanguage(code)?.voices[0] ?? code;
          utterance.voice = voice;
          utterance.rate = 1;
          utterance.volume = 1;
          utterance.onend = end;
          utterance.onerror = end;
          if (synth.paused) synth.resume?.();
          synth.speak(utterance);
          timer = later(end, floorMs + words.length * msPerChar);
        } catch {
          end();
        }
      });
    },
    cancel() {
      try {
        synth.cancel();
      } catch {
        /* nothing to stop */
      }
      for (const end of Array.from(open)) end();
    },
  };
}

type SpeechScope = {
  speechSynthesis?: SynthLike;
  SpeechSynthesisUtterance?: UtteranceCtor;
};

/** The browser's read-aloud, or null where it has none. */
export function browserSpeechOut(scope: object = globalThis): SpeechOut | null {
  const s = scope as SpeechScope;
  if (!s.speechSynthesis || typeof s.SpeechSynthesisUtterance !== 'function') return null;
  return createSpeechOut(s.speechSynthesis, s.SpeechSynthesisUtterance);
}
